/**
 * SPJ-1726 / SPJ-1728: selezione di un negozio cercabile per codice o nome, con filtro per
 * nazione. Sostituisce la <select> nativa con centinaia di negozi della creazione progetto.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { performAction } from '../api/sophia';

export type ShopOption = { code: string; name: string; nazione?: string };

// SPJ-1728: nazione per codice negozio, letta una volta per sessione (list-available-shops).
let shopNazioneCache: Promise<Record<string, string>> | null = null;

export function useShopNazione(threadId?: string | null, messageId?: string | null): Record<string, string> {
  const [nazioni, setNazioni] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!threadId || !messageId) return;
    if (!shopNazioneCache) {
      shopNazioneCache = performAction({ threadId, messageId, actionName: 'list-available-shops' })
        .then((res) => ((res as any)?.['global-page-data']?.['shops-nazione'] || {}) as Record<string, string>)
        .catch(() => {
          shopNazioneCache = null;
          return {};
        });
    }
    let alive = true;
    void shopNazioneCache.then((map) => { if (alive) setNazioni(map); });
    return () => { alive = false; };
  }, [threadId, messageId]);
  return nazioni;
}

/** Nazione di un negozio o di un gruppo "A|,|B" (solo se tutti i negozi sono della stessa). */
export function nazioneOfShopId(shopId: string, shopNazione: Record<string, string>): string | undefined {
  const codes = shopId.split('|,|').map((c) => c.trim()).filter(Boolean);
  const nazioni = new Set(codes.map((c) => shopNazione[c]));
  return nazioni.size === 1 ? [...nazioni][0] : undefined;
}

/** Ricerca + filtro nazione sopra una lista di negozi (tab o pulsanti). */
export function ShopListFilter({
  search,
  onSearch,
  nazione,
  onNazione,
  nazioni,
  visible,
  total,
}: {
  search: string;
  onSearch: (value: string) => void;
  nazione: string;
  onNazione: (value: string) => void;
  nazioni: string[];
  visible: number;
  total: number;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        placeholder="Cerca negozio..."
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        className="min-w-0 flex-1 border border-gray-300 px-2 py-1 text-xs focus:border-[#9F6460] focus:outline-none"
      />
      <NazioneFilter nazioni={nazioni} value={nazione} onChange={onNazione} compact />
      {(search || nazione) && <span className="shrink-0 text-xs text-gray-500">{visible}/{total}</span>}
    </div>
  );
}

/** Nazioni presenti nelle opzioni, ordinate, per il filtro. */
export function nazioniOf(options: { nazione?: string }[]): string[] {
  return Array.from(new Set(options.map((o) => o.nazione).filter((n): n is string => !!n))).sort();
}

/** Filtro comune: testo su codice o nome, e nazione (vuota = tutte). */
export function matchesShop(option: ShopOption, search: string, nazione: string): boolean {
  if (nazione && option.nazione !== nazione) return false;
  const term = search.trim().toLowerCase();
  if (!term) return true;
  return option.code.toLowerCase().includes(term) || option.name.toLowerCase().includes(term);
}

export function NazioneFilter({
  nazioni,
  value,
  onChange,
  disabled,
  compact,
}: {
  nazioni: string[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  if (nazioni.length === 0) return null;
  return (
    <select
      aria-label="Filtra per nazione"
      title="Filtra per nazione"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`border border-gray-300 focus:border-[#9F6460] focus:outline-none disabled:bg-gray-100 ${compact ? 'px-1.5 py-1 text-xs' : 'px-2 py-2 text-sm'}`}
    >
      <option value="">Tutte le nazioni</option>
      {nazioni.map((n) => (
        <option key={n} value={n}>{n}</option>
      ))}
    </select>
  );
}

export function ShopPicker({
  options,
  value,
  onChange,
  disabled,
  placeholder = 'Cerca negozio per codice o nome',
  compact,
}: {
  options: ShopOption[];
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  placeholder?: string;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [nazione, setNazione] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.code === value);
  const nazioni = useMemo(() => nazioniOf(options), [options]);
  const filtered = useMemo(
    () => options.filter((o) => matchesShop(o, search, nazione)),
    [options, search, nazione],
  );

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const choose = (code: string) => {
    onChange(code);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={rootRef} className="relative">
      <div>
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          placeholder={selected ? `${selected.code} - ${selected.name}` : placeholder}
          value={open ? search : selected ? `${selected.code} - ${selected.name}` : ''}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter' && filtered.length > 0) {
              e.preventDefault();
              choose(filtered[0].code);
            }
          }}
          className={`w-full border border-gray-300 focus:border-[#9F6460] focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100 ${compact ? 'rounded px-2 py-1 text-xs' : 'px-3 py-2'}`}
        />
      </div>
      {open && !disabled && (
        <div className={`absolute z-30 mt-1 border border-gray-300 bg-white shadow-lg ${compact ? 'min-w-[280px]' : 'w-full'}`}>
        {nazioni.length > 0 && (
          <div className="flex items-center gap-2 border-b border-gray-100 px-2 py-1.5" onMouseDown={(e) => e.stopPropagation()}>
            <NazioneFilter nazioni={nazioni} value={nazione} onChange={setNazione} compact />
            <span className="text-xs text-gray-500">{filtered.length} negozi</span>
          </div>
        )}
        <ul role="listbox" className="max-h-64 overflow-y-auto">
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm italic text-gray-500">Nessun negozio trovato</li>
          ) : (
            filtered.map((o) => (
              <li
                key={o.code}
                role="option"
                aria-selected={o.code === value}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(o.code);
                }}
                className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 ${o.code === value ? 'bg-[#9F6460]/10' : ''}`}
              >
                <span className="w-12 shrink-0 font-mono text-xs font-semibold text-gray-900">{o.code}</span>
                <span className="truncate text-gray-700">{o.name}</span>
                {o.nazione && <span className="ml-auto shrink-0 text-xs text-gray-400">{o.nazione}</span>}
              </li>
            ))
          )}
        </ul>
        </div>
      )}
    </div>
  );
}
